import { FormEvent, useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router'
import { DateTime } from 'luxon'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { api, ApiError } from '../../api/client.js'
import { useAuth } from '../../auth/AuthContext.js'
import {
  AdminCard,
  AdminPageHeader,
  AdminTableEmpty,
  adminTableSx,
} from '../../components/AdminUi.js'
import type { AdminOutletContext } from './AdminLayout.js'

const orgErrorMessage = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.message === 'user_not_found') return 'Kein Nutzer mit dieser E-Mail-Adresse gefunden.'
    if (err.message === 'invalid_body') return 'Bitte einen Namen angeben.'
  }
  return 'Anlegen fehlgeschlagen'
}

/**
 * Einstellungen → Organisationen: Übersicht und (nur Systemadmins) Anlegen.
 * Org-Admins sehen hier ausschließlich ihre eigenen Organisationen — ohne
 * Owner-/Zähler-Spalten, die nur die Systemadmin-Liste liefert. Alles
 * Org-spezifische (Mitglieder, Gruppen, MCP-Server, API-Schlüssel, Teams,
 * Slack) liegt in der Detailansicht (AdminOrganizationDetail).
 */
export const AdminOrganizationsSection = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isSystemAdmin = user?.role === 'system-admin'
  // Organisationsliste kommt zentral aus dem AdminLayout (speist dort auch
  // die Menü-Ordner) — nach dem Anlegen dort aktualisieren.
  const { organizations, reloadOrganizations } = useOutletContext<AdminOutletContext>()

  const [creatingOrg, setCreatingOrg] = useState(false)
  const [newOrgName, setNewOrgName] = useState('')
  const [newOrgOwnerEmail, setNewOrgOwnerEmail] = useState('')
  const [orgError, setOrgError] = useState<string | undefined>(undefined)

  const handleCreateOrg = async (event: FormEvent) => {
    event.preventDefault()
    setOrgError(undefined)
    try {
      const org = await api.createOrganizationAsAdmin(newOrgName, newOrgOwnerEmail || undefined)
      setNewOrgName('')
      setNewOrgOwnerEmail('')
      setCreatingOrg(false)
      reloadOrganizations()
      navigate(`/settings/organizations/${org.id}/general`)
    } catch (err) {
      setOrgError(orgErrorMessage(err))
    }
  }

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <AdminPageHeader
        title="Organisationen"
        description="Organisationen bündeln Mitglieder, Gruppen, MCP-Server und Integrationen. Details über die Zeile oder das Menü links."
        actions={
          isSystemAdmin ? (
            <Button variant="contained" onClick={() => setCreatingOrg(true)}>
              Neue Organisation
            </Button>
          ) : undefined
        }
      />

      <AdminCard title={`Alle Organisationen (${organizations.length})`}>
        <Table size="small" sx={adminTableSx}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              {isSystemAdmin && <TableCell>Eigentümer</TableCell>}
              {isSystemAdmin && <TableCell>Mitglieder</TableCell>}
              {isSystemAdmin && <TableCell>Gruppen</TableCell>}
              <TableCell>Angelegt</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {organizations.map((org) => (
              <TableRow
                key={org.id}
                hover
                onClick={() => navigate(`/settings/organizations/${org.id}/general`)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{org.name}</TableCell>
                {isSystemAdmin && (
                  <TableCell>
                    {org.ownerDisplayName}
                    <Typography variant="caption" color="text.secondary" display="block">
                      {org.ownerEmail}
                    </Typography>
                  </TableCell>
                )}
                {isSystemAdmin && <TableCell>{org.memberCount}</TableCell>}
                {isSystemAdmin && <TableCell>{org.groupCount}</TableCell>}
                <TableCell>{DateTime.fromISO(org.createdAt).toFormat('dd.MM.yyyy')}</TableCell>
              </TableRow>
            ))}
            {organizations.length === 0 && (
              <AdminTableEmpty colSpan={isSystemAdmin ? 5 : 2}>
                Keine Organisationen vorhanden.
              </AdminTableEmpty>
            )}
          </TableBody>
        </Table>
      </AdminCard>

      <Dialog open={creatingOrg} onClose={() => setCreatingOrg(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Neue Organisation</DialogTitle>
        <DialogContent>
          {orgError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {orgError}
            </Alert>
          )}
          <Box component="form" onSubmit={handleCreateOrg} display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Name"
              size="small"
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              required
              autoFocus
            />
            <TextField
              label="Owner (E-Mail, optional)"
              type="email"
              size="small"
              value={newOrgOwnerEmail}
              onChange={(e) => setNewOrgOwnerEmail(e.target.value)}
              helperText="Ohne Angabe wirst du selbst Owner."
            />
            <Button type="submit" variant="contained">
              Anlegen
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  )
}
